import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const TID_STORAGE_KEY = 'tinypdfs:clickbank_tid';
const CLICKBANK_HOST = 'hop.clickbank.net';

function sanitizeTid(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24);
}

function readStoredTid() {
  try {
    return sanitizeTid(window.sessionStorage.getItem(TID_STORAGE_KEY));
  } catch {
    return '';
  }
}

function storeTid(tid) {
  try {
    if (tid) window.sessionStorage.setItem(TID_STORAGE_KEY, tid);
  } catch {
    // Session storage can be blocked in hardened browsers.
  }
}

function resolveTid() {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search);
  const tidFromUrl = sanitizeTid(params.get('tid'));
  if (tidFromUrl) {
    storeTid(tidFromUrl);
    return tidFromUrl;
  }

  return readStoredTid();
}

function isClickBankHopLink(url) {
  return url.hostname.toLowerCase() === CLICKBANK_HOST;
}

function appendTidToClickBankLinks(tid) {
  if (!tid || typeof document === 'undefined') return;

  document.querySelectorAll('a[href*="hop.clickbank.net"]').forEach((anchor) => {
    try {
      const url = new URL(anchor.getAttribute('href'), window.location.href);
      if (!isClickBankHopLink(url)) return;

      if (url.searchParams.get('tid') !== tid) {
        url.searchParams.set('tid', tid);
        anchor.href = url.toString();
      }
    } catch {
      // Ignore malformed or non-URL href values.
    }
  });
}

function trackClickBankHopClick(anchor, tid) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;

  window.fbq('trackCustom', 'ClickBankHopLink', {
    tid: tid || '',
    destination_host: CLICKBANK_HOST,
    destination_path: anchor.pathname || '/',
  });
}

function ClickBankTidTracker() {
  const location = useLocation();

  useEffect(() => {
    const applyTid = () => appendTidToClickBankLinks(resolveTid());

    applyTid();

    const observer = new MutationObserver(applyTid);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    const handleClick = (event) => {
      const anchor = event.target?.closest?.('a[href*="hop.clickbank.net"]');
      if (!anchor) return;

      const tid = resolveTid();
      appendTidToClickBankLinks(tid);
      trackClickBankHopClick(anchor, tid);
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClick, true);
    };
  }, [location.pathname, location.search]);

  return null;
}

export default ClickBankTidTracker;
